import { useState, type FormEvent } from 'react';
import {
  AVATARS_BY_ID,
  DICE_CATALOG,
  PACK_PRICE_LABEL,
  type AppConfig,
  type AvatarId,
  type EntitlementsView,
  type GameSettings,
  type PublicRoomSummary,
} from '@shared/game/types';
import type { Preferences } from '../lib/preferences';
import type { CtaLocation } from '../lib/attribution';
import { Avatar } from './Avatar';
import { AvatarPicker } from './AvatarPicker';
import { Brand } from './Brand';
import { CreateRoomModal } from './CreateRoomModal';
import { Dice } from './Dice';
import { Icon } from './Icon';
import { InstallPwaButton } from './InstallPwaButton';
import { modeLabel } from '../lib/format';

interface HomeScreenProps {
  preferences: Preferences;
  onPreferencesChange: (preferences: Preferences) => void;
  rooms: PublicRoomSummary[];
  connected: boolean;
  busy: boolean;
  config: AppConfig | null;
  entitlements: EntitlementsView;
  unlockedAvatars: AvatarId[];
  onQuick: () => void;
  onCreate: (settings: GameSettings) => void;
  onJoin: (code: string) => void;
  onDemo: () => void;
  onRules: () => void;
  onTutorial: () => void;
  onShop: (location?: CtaLocation) => void;
  onSettings: () => void;
  onRefresh: () => void;
}

export function HomeScreen({
  preferences,
  onPreferencesChange,
  rooms,
  connected,
  busy,
  config,
  entitlements,
  unlockedAvatars,
  onQuick,
  onCreate,
  onJoin,
  onDemo,
  onRules,
  onTutorial,
  onShop,
  onSettings,
  onRefresh,
}: HomeScreenProps) {
  const [code, setCode] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatar = AVATARS_BY_ID[preferences.avatar];
  const hasAstralFrame = entitlements.cosmetics.includes('profile-frame-astral');
  const hasExplorerBadge = entitlements.cosmetics.includes('badge-first-explorer');
  const hasAstralDice = entitlements.cosmetics.includes('dice-effect-astral');
  const packLocation = (): CtaLocation => (window.innerWidth <= 760 ? 'mobile' : 'hero');

  const submitJoin = (event: FormEvent) => {
    event.preventDefault();
    const cleaned = code.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (cleaned.length >= 4) onJoin(cleaned);
  };

  return (
    <div className="app app--home">
      <header className="topbar">
        <Brand />
        <div className="topbar__actions">
          <span className={`connection-pill ${connected ? 'is-online' : 'is-offline'}`}>
            <Icon name={connected ? 'wifi' : 'wifi-off'} size={14} />
            {connected ? 'En línea' : 'Reconectando'}
          </span>
          <button type="button" className="icon-button" onClick={() => onPreferencesChange({ ...preferences, sound: !preferences.sound })} aria-label={preferences.sound ? 'Silenciar efectos' : 'Activar efectos'}>
            <Icon name={preferences.sound ? 'volume' : 'volume-off'} size={19} />
          </button>
          <button type="button" className={`icon-button ${preferences.music ? 'is-active' : ''}`} onClick={() => onPreferencesChange({ ...preferences, music: !preferences.music })} aria-label={preferences.music ? 'Desactivar música' : 'Activar música'}>
            <Icon name="music" size={19} />
          </button>
          <button type="button" className="icon-button" onClick={onSettings} aria-label="Ajustes">
            <Icon name="settings" size={19} />
          </button>
          <button type="button" className="icon-button icon-button--accent" onClick={() => onShop('shop')} aria-label="Cosméticos">
            <Icon name="bag" size={19} />
          </button>
        </div>
      </header>

      <main className="home">
        <section className="hero">
          <div className="hero__copy">
            <span className="season-tag"><span>Temporada III</span> Protocolo Núcleo</span>
            <span className="eyebrow"><span className="pulse-dot" /> Parchís online · 2 a 4 jugadores</span>
            <h1>El núcleo<br /><em>despierta.</em></h1>
            <p className="hero__lead">
              Cuatro Nodos. Una arena de cristal suspendida sobre la ciudad. Unidades de energía compiten por
              sincronizar sus fichas con el Núcleo antes de que el portal se cierre. Salas privadas, partidas
              públicas y anomalías que convierten cada tirada en una escena.
            </p>
            <div className="hero__actions">
              <button type="button" className="button button--primary button--large" onClick={onQuick} disabled={busy || !connected}>
                <Icon name="play" size={19} /> Jugar ahora <span className="button__tag">1v1</span>
              </button>
              <button type="button" className="button button--secondary button--large" onClick={() => setCreateOpen(true)} disabled={busy || !connected}>
                <Icon name="users" size={18} /> Crear arena
              </button>
              <InstallPwaButton className="button--large" />
            </div>
            <div className="hero__meta">
              <span><Icon name="check" size={14} /> Sin registro</span>
              <span><Icon name="shield" size={14} /> Juego gratis</span>
              <span><Icon name="wifi" size={14} /> Reconexión</span>
            </div>
            <button type="button" className="hero-pack-link" onClick={() => onShop(packLocation())}>
              <Icon name="star" size={14} />
              Pack cosmético
              <strong>1,99 €</strong>
              <Icon name="chevron-right" size={14} />
            </button>
          </div>

          <div className="hero-art" aria-hidden="true">
            <div className="hero-art__glow" />
            <div className="hero-art__island" />
            <div className="hero-art__rift" />
            <div className="hero-art__orbit hero-art__orbit--outer" />
            <div className="hero-art__orbit hero-art__orbit--inner" />
            <div className="hero-art__spark hero-art__spark--one">✦</div>
            <div className="hero-art__spark hero-art__spark--two">✧</div>
            <div className="hero-art__avatar hero-art__avatar--one"><Avatar avatar="volt" size={78} decorative /></div>
            <div className="hero-art__avatar hero-art__avatar--two"><Avatar avatar="moss" size={66} decorative /></div>
            <div className="hero-art__avatar hero-art__avatar--three"><Avatar avatar="lumen" size={66} decorative /></div>
            <div className="hero-art__dice">
              <Dice value={5} skin={preferences.diceSkin} animations={preferences.animations} size="normal" effect={hasAstralDice ? 'astral' : undefined} />
            </div>
            <div className="hero-art__plaque">
              <span className="hero-art__plaque-icon"><Icon name="portal" size={15} /></span>
              <span><strong>Protocolo Núcleo</strong><small>Portales, cofres y anomalías</small></span>
            </div>
          </div>
        </section>

        <section className="ai-pass-home" aria-label="Pase de partida con agentes IA">
          <div className="ai-pass-home__copy">
            <span className="eyebrow">Pase IA</span>
            <h2>Juega con hasta 2 agentes IA</h2>
            <p>
              Crea una arena 2+2 y desbloquea dos asientos de agente durante esa partida.
              Los bots locales siguen siendo gratuitos y el pack cosmético no incluye este pase.
            </p>
            <div className="ai-pass-home__price">
              <strong>1,99 €</strong>
              <span>pago único · hasta 2 agentes · 12 turnos por agente</span>
            </div>
            <div className="ai-pass-home__actions">
              <button type="button" className="button button--primary" onClick={() => setCreateOpen(true)}>
                <Icon name="bot" size={16} /> Crear arena 2+2
              </button>
              <span>El anfitrión activa el pase en la sala de espera.</span>
            </div>
          </div>
          <div className="ai-pass-home__art" aria-hidden="true">
            <span className="ai-pass-home__orb" />
            <Icon name="bot" size={54} />
            <span className="ai-pass-home__seal">IA</span>
          </div>
        </section>

        <section className="pack-spotlight" aria-label="Pack cosmético del Viajero Astral">
          <div className="pack-spotlight__copy">
            <span className="eyebrow">Edición cosmética opcional</span>
            <h2>Pack del Viajero Astral</h2>
            <p>
              Apoya el proyecto y desbloquea la edición cosmética del Viajero Astral.
              El juego sigue siendo gratis y el pack no añade ventajas competitivas.
            </p>
            <div className="pack-spotlight__price">{PACK_PRICE_LABEL}</div>
            <div className="pack-spotlight__actions">
              <button type="button" className="button button--primary" onClick={() => onShop(packLocation())}>
                <Icon name="bag" size={17} /> Descubrir el pack
              </button>
              <span>7 cosméticos · pago único</span>
            </div>
          </div>
          <div className="pack-spotlight__art" aria-hidden="true">
            <div className="pack-spotlight__avatar">
              <Avatar avatar="solstice" size={92} />
              <span className="cosmetic-badge"><Icon name="star" size={11} /> Primer explorador</span>
            </div>
            <div className="pack-spotlight__dice">
              <Dice value={6} skin="aurora" animations={false} size="small" effect="astral" />
              <Dice value={3} skin="aurora" animations={false} size="small" effect="astral" />
            </div>
          </div>
        </section>

        <section className="home-grid">
          <aside className="passport card">
            <div className="card__header">
              <span className="eyebrow">Tu pasaporte</span>
              <button type="button" className="icon-button icon-button--small" onClick={onSettings} aria-label="Abrir ajustes">
                <Icon name="settings" size={16} />
              </button>
            </div>
            <div className="passport__identity">
              <button type="button" className={`passport__avatar ${hasAstralFrame ? 'passport__avatar--astral' : ''}`} onClick={() => setAvatarOpen((open) => !open)} aria-label="Cambiar avatar">
                <Avatar avatar={preferences.avatar} size={82} />
                <span className="passport__edit"><Icon name="refresh" size={13} /></span>
              </button>
              <div className="passport__name">
                <label htmlFor="player-name">Nombre de viajero</label>
                <input
                  id="player-name"
                  value={preferences.name}
                  maxLength={20}
                  onChange={(event) => onPreferencesChange({ ...preferences, name: event.target.value })}
                  onBlur={() => {
                    if (!preferences.name.trim()) {
                      onPreferencesChange({ ...preferences, name: 'Viajero' });
                    }
                  }}
                  placeholder="Tu nombre"
                />
                <span className="passport__tagline">{avatar.tagline}</span>
                {hasExplorerBadge && (
                  <span className="cosmetic-badge"><Icon name="star" size={12} /> Primer explorador</span>
                )}
              </div>
            </div>

            {avatarOpen && (
              <div className="passport__avatars">
                <AvatarPicker
                  selected={preferences.avatar}
                  unlocked={unlockedAvatars}
                  onSelect={(next) => {
                    onPreferencesChange({ ...preferences, avatar: next });
                    setAvatarOpen(false);
                  }}
                  compact
                />
              </div>
            )}

            <div className="passport__dice">
              <Dice value={4} skin={preferences.diceSkin} animations={false} size="small" effect={hasAstralDice ? 'astral' : undefined} />
              <div>
                <strong>Dado {DICE_CATALOG.find((dice) => dice.id === preferences.diceSkin)?.name ?? 'Obsidiana'}</strong>
                <button type="button" className="text-link" onClick={onSettings}>Cambiar diseño</button>
              </div>
              <button type="button" className="icon-button icon-button--small" onClick={() => onPreferencesChange({ ...preferences, sound: !preferences.sound })} aria-label={preferences.sound ? 'Silenciar' : 'Activar sonido'}>
                <Icon name={preferences.sound ? 'volume' : 'volume-off'} size={16} />
              </button>
            </div>

            <p className="passport__privacy">
              <Icon name="lock" size={13} /> Sin registro. Tu sesión se conserva en este navegador.
            </p>
          </aside>

          <div className="home-actions">
            <div className="action-banner action-banner--adventure">
              <div className="action-banner__icon"><Icon name="portal" size={22} /></div>
              <div>
                <span className="eyebrow">Arena activa</span>
                <h2>Sincroniza el Núcleo</h2>
                <p>Portales, cofres y anomalías. O desactiva el protocolo y juega al clásico.</p>
              </div>
              <button type="button" className="button button--secondary" onClick={() => setCreateOpen(true)}>Abrir arena <Icon name="chevron-right" size={16} /></button>

            </div>

            <div className="quick-grid">
              <button type="button" className="quick-card" onClick={onQuick} disabled={busy || !connected}>
                <span className="quick-card__icon"><Icon name="globe" size={21} /></span>
                <strong>Sesión pública</strong>
                <span>Emparejamiento rápido 1 contra 1.</span>
              </button>
              <button type="button" className="quick-card" onClick={onDemo} disabled={busy || !connected}>
                <span className="quick-card__icon"><Icon name="bot" size={21} /></span>
                <strong>Probar con bots</strong>
                <span>Tres unidades de prueba. Ideal para aprender.</span>
              </button>
              <button type="button" className="quick-card" onClick={onTutorial}>
                <span className="quick-card__icon"><Icon name="play" size={21} /></span>
                <strong>Tutorial de juego</strong>
                <span>Aprende a salir, tirar, capturar y ganar.</span>
              </button>
              <button type="button" className="quick-card" onClick={() => onShop(packLocation())}>
                <span className="quick-card__icon"><Icon name="star" size={21} /></span>
                <strong>Cosméticos</strong>
                <span>{config?.paymentsEnabled ? 'Pack Cosmético disponible.' : 'Unidades y dados holográficos.'}</span>
              </button>
            </div>
          </div>
        </section>

        <section className="join-room card">
          <div>
            <span className="eyebrow">¿Te han pasado un código?</span>
            <h2>Entra en la arena</h2>
            <p>Los códigos tienen 6 caracteres. También puedes abrir directamente el enlace de invitación.</p>
          </div>
          <form className="join-form" onSubmit={submitJoin}>
            <input
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
              placeholder="CÓDIGO"
              aria-label="Código de sala"
              autoComplete="off"
              spellCheck={false}
            />
            <button type="submit" className="button button--primary" disabled={code.trim().length < 4 || busy || !connected}>
              Entrar <Icon name="chevron-right" size={17} />
            </button>
          </form>
        </section>

        <section className="rooms-section">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Red pública</span>
              <h2>Arenas abiertas</h2>
            </div>
            <button type="button" className="button button--ghost button--small" onClick={onRefresh}>
              <Icon name="refresh" size={15} /> Actualizar
            </button>
          </div>
          {rooms.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__icon"><Icon name="users" size={25} /></div>
              <h3>No hay arenas públicas esperando</h3>
              <p>Crea una arena pública o usa «Jugar ahora» para abrir una sesión rápida.</p>
              <button type="button" className="button button--secondary" onClick={() => setCreateOpen(true)}>
                <Icon name="plus" size={16} /> Crear sala pública
              </button>
            </div>
          ) : (
            <div className="room-grid">
              {rooms.map((room) => (
                <article key={room.code} className="room-card">
                  <div className="room-card__top">
                    <span className={`mode-badge mode-badge--${room.mode}`}>
                      <Icon name={room.mode === 'adventure' ? 'spark' : 'sword'} size={13} />
                      {modeLabel(room.mode)}
                    </span>
                    <span className="room-card__players"><Icon name="users" size={14} /> {room.playerCount}/{room.maxPlayers}</span>
                  </div>
                  <h3>Arena de {room.hostName}</h3>
                  <p>
                    {room.mode === 'adventure' && room.effectsEnabled ? 'Anomalías activas' : 'Reglas clásicas'} ·{' '}
                    {room.humanCount} {room.humanCount === 1 ? 'persona' : 'personas'}
                  </p>
                  <div className="room-card__footer">
                    <span className="room-code">{room.code}</span>
                    <button type="button" className="button button--secondary button--small" onClick={() => onJoin(room.code)}>
                      Unirme <Icon name="chevron-right" size={14} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="features">
          <div className="feature">
            <span className="feature__number">01</span>
            <h3>Salas privadas por enlace</h3>
            <p>Comparte un código de 6 caracteres y elegid escuadrón antes de aterrizar.</p>
          </div>
          <div className="feature">
            <span className="feature__number">02</span>
            <h3>Un cielo que no hace trampas</h3>
            <p>Dados criptográficos, turnos y movimientos validados en el servidor. Tu móvil solo dibuja la arena.</p>
          </div>
          <div className="feature">
            <span className="feature__number">03</span>
            <h3>Tu identidad, tus reglas</h3>
            <p>Unidades de energía, dados de cristal y escenas de salto. Sonido, música y animaciones desactivables.</p>
          </div>
        </section>
      </main>

      <footer className="footer">
        <Brand compact />
        <p>Un parchís original de unidades y energía. Sin personajes de franquicias.</p>
        <div className="footer__links">
          <button type="button" className="text-link" onClick={onTutorial}>Tutorial</button>
          <button type="button" className="text-link" onClick={onRules}>Reglas</button>
          <button type="button" className="text-link" onClick={() => onShop('shop')}>Cosméticos</button>
          <button type="button" className="text-link" onClick={onSettings}>Ajustes</button>
        </div>
      </footer>

      <CreateRoomModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        busy={busy}
        onCreate={(settings) => {
          setCreateOpen(false);
          onCreate(settings);
        }}
      />
    </div>
  );
}
